/**
 * ErrorBoundary
 * Keeps a part of the page that fails to render (or to download) from blanking the
 * whole app.
 *
 *   <ErrorBoundary reloadOnChunkError>…</ErrorBoundary>  — whole app: shows a reload message
 *   <ErrorBoundary fallback={null}>…</ErrorBoundary>     — one tool: it just disappears
 *
 * reloadOnChunkError: a page piece that fails to download is most often an old tab
 * asking for files a newer release replaced, so the page reloads once to get it.
 * chunkErrorMessage: a tool that disappears because its code failed to download says
 * so (with a Reload button), instead of its button silently doing nothing.
 * chunkErrorOpenEvent: the window event that opens the tool; the message then shows
 * when someone tries to open it, rather than as the page loads.
 */

import React from 'react';
import { toast } from 'sonner';
import { Button } from '@/Components/ui/button';

const RELOAD_KEY = 'chunkErrorReloadAt';

export const isChunkLoadError = (error) =>
  error?.name === 'ChunkLoadError'
  || /Loading (CSS )?chunk [\w-]+ failed|dynamically imported module/i.test(error?.message || '');

const reloadOnce = () => {
  try {
    if (Date.now() - Number(sessionStorage.getItem(RELOAD_KEY) || 0) < 60000) return;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return;
  }
  window.location.reload();
};

const PageError = () => (
  <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 p-6 text-center">
    <p className="text-base font-medium text-slate-800">This page didn't load properly.</p>
    <p className="text-sm text-slate-500">Reloading usually fixes it. Saved work is not affected.</p>
    <Button onClick={() => window.location.reload()}>Reload page</Button>
  </div>
);

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error('[ErrorBoundary]', error, info?.componentStack);
    if (this.props.reloadOnChunkError && isChunkLoadError(error)) reloadOnce();
    if (this.props.chunkErrorMessage && isChunkLoadError(error)) {
      const { chunkErrorOpenEvent } = this.props;
      if (chunkErrorOpenEvent) {
        if (!this.showOnOpen) {
          this.showOnOpen = () => this.showChunkErrorMessage();
          window.addEventListener(chunkErrorOpenEvent, this.showOnOpen);
        }
      } else {
        this.showChunkErrorMessage();
      }
    }
  }

  componentWillUnmount() {
    if (this.showOnOpen) window.removeEventListener(this.props.chunkErrorOpenEvent, this.showOnOpen);
  }

  showChunkErrorMessage() {
    // One message per tool, so two tools that failed don't replace each other's
    toast.error(this.props.chunkErrorMessage, {
      id: `chunk-error-${this.props.chunkErrorOpenEvent || this.props.chunkErrorMessage}`,
      action: { label: 'Reload', onClick: () => window.location.reload() },
    });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return this.props.fallback !== undefined ? this.props.fallback : <PageError />;
  }
}

export default ErrorBoundary;
